import React, { useState, useCallback, useRef, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import { translateDomain } from '../../utils/labels_utils'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import {
  Spinner,
} from 'Components/common'
import {
  getAssessmentByToken,
  updateVendorResponse,
  uploadEvidence,
  submitVendorAssessment
} from 'services/vendor-assessment-service'
import { getPublicBranding } from 'services/company-service'
import { Dialog, TextField } from 'Components/ui'
import {
  GppGoodIcon,
  GppBadIcon,
  GppMaybeIcon,
  CloudUploadIcon,
  SendIcon,
  LockIcon,
  RefreshIcon,
} from './AssessmentIcons'
import {
  PageWrapper,
  ExternalHeader,
  HeaderLeft,
  HeaderLogo,
  HeaderDivider,
  HeaderCompanyInfo,
  HeaderRequestedLabel,
  HeaderCompanyName,
  HeaderRight,
  HeaderDot,
  HeaderLinkStatus,
  PageContainer,
  LoadingWrapper,
  ErrorContainer,
  ErrorTitle,
  ErrorText,
  Section,
  InfoCard,
  HeaderTitle,
  HeaderMeta,
  ProgressSection,
  ProgressLabelRow,
  ProgressLabel,
  ProgressValue,
  ProgressTrack,
  ProgressFill,
  InstructionAlert,
  InstructionText,
  AccordionCard,
  AccordionHeader,
  AccordionHeaderLeft,
  DomainName,
  DomainProgressBadge,
  ChevronWrapper,
  AccordionBody,
  ControlWrapper,
  ControlHeader,
  ControlTitle,
  LockedBadge,
  MaturityRadioGroup,
  RadioLabel,
  RadioInput,
  LevelLabel,
  LevelDesc,
  EvidenceSection,
  EvidenceLabel,
  StyledUploadButton,
  RefreshButton,
  FileList,
  FileItem,
  FileIconWrapper,
  FileDetails,
  FileName,
  FileMeta,
  ScanChip,
  ActionRow,
  SavingIndicator,
  SubmitButton,
} from './VendorAssessmentForm.styles'

const str = (val) => {
  if (!val) return ''
  if (typeof val === 'object') {
    const lang = (i18n.language || 'en').split('-')[0]
    const raw = val[lang] || val.en || val.pt || val.es || Object.values(val)[0] || ''
    return typeof raw === 'string' ? raw : String(raw)
  }
  return String(val)
}

const getScanStatusDisplay = (scanStatus, t) => {
  if (!scanStatus) {
    return {
      icon: <GppGoodIcon width="16" height="16" />,
      color: 'var(--vo-success)',
      label: t('gap-analysis:assessment.vendorForm.scanStatus.verified'),
      chipColor: 'success'
    }
  }
  switch (scanStatus) {
    case 'clean':
      return {
        icon: <GppGoodIcon width="16" height="16" />,
        color: 'var(--vo-success)',
        label: t('gap-analysis:assessment.vendorForm.scanStatus.scanPassed'),
        chipColor: 'success'
      }
    case 'malicious':
      return {
        icon: <GppBadIcon width="16" height="16" />,
        color: 'var(--vo-danger)',
        label: t('gap-analysis:assessment.vendorForm.scanStatus.threatDetected'),
        chipColor: 'error'
      }
    case 'error':
      return {
        icon: <GppMaybeIcon width="16" height="16" />,
        color: 'var(--vo-warning)',
        label: t('gap-analysis:assessment.vendorForm.scanStatus.scanError'),
        chipColor: 'warning'
      }
    case 'pending':
    default:
      return {
        icon: <GppMaybeIcon width="16" height="16" />,
        color: 'var(--vo-slate)',
        label: t('gap-analysis:assessment.vendorForm.scanStatus.scanning'),
        chipColor: 'default'
      }
  }
}

const InfoCircleSVG = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--vo-celeste)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
)

const ChevronSVG = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
)

export const VendorAssessmentForm = () => {
  const { t } = useTranslation(['gap-analysis'])
  const { token } = useParams()

  const maturityLevels = [
    { value: 0, label: t('gap-analysis:assessment.vendorForm.maturityLevels.l0.label'), description: t('gap-analysis:assessment.vendorForm.maturityLevels.l0.description') },
    { value: 1, label: t('gap-analysis:assessment.vendorForm.maturityLevels.l1.label'), description: t('gap-analysis:assessment.vendorForm.maturityLevels.l1.description') },
    { value: 2, label: t('gap-analysis:assessment.vendorForm.maturityLevels.l2.label'), description: t('gap-analysis:assessment.vendorForm.maturityLevels.l2.description') },
    { value: 3, label: t('gap-analysis:assessment.vendorForm.maturityLevels.l3.label'), description: t('gap-analysis:assessment.vendorForm.maturityLevels.l3.description') },
    { value: 4, label: t('gap-analysis:assessment.vendorForm.maturityLevels.l4.label'), description: t('gap-analysis:assessment.vendorForm.maturityLevels.l4.description') },
    { value: 5, label: t('gap-analysis:assessment.vendorForm.maturityLevels.l5.label'), description: t('gap-analysis:assessment.vendorForm.maturityLevels.l5.description') },
  ]
  const queryClient = useQueryClient()

  const [expandedDomainIndex, setExpandedDomainIndex] = useState(null)
  const [localNotes, setLocalNotes] = useState({})
  const debounceTimers = useRef({})
  const accordionRefs = useRef({})

  const toggleDomain = useCallback((domainIdx) => {
    setExpandedDomainIndex(prev => {
      const opening = prev !== domainIdx
      if (opening) {
        requestAnimationFrame(() => {
          const el = accordionRefs.current[domainIdx]
          if (el) {
            const top = el.getBoundingClientRect().top + window.scrollY - 88
            window.scrollTo({ top, behavior: 'smooth' })
          }
        })
      }
      return opening ? domainIdx : null
    })
  }, [])

  const { data: assessmentData, isLoading: assessmentLoading } = useQuery(
    ['vendor-assessment', token],
    () => getAssessmentByToken(token),
    {
      enabled: !!token,
      retry: false,
      onError: () => {
        Dialog.alert({
          icon: 'error',
          title: t('gap-analysis:assessment.vendorForm.swal.invalidTitle'),
          message: t('gap-analysis:assessment.vendorForm.swal.invalidText')
        })
      }
    }
  )

  const assessment = assessmentData?.data?.data?.assessment
  const controls = assessmentData?.data?.data?.controlResponses || []
  const companyKey = assessment?.companyKey

  const { data: brandingData } = useQuery(
    ['publicBranding', companyKey],
    () => getPublicBranding(companyKey),
    { enabled: !!companyKey, retry: false, staleTime: 5 * 60 * 1000 }
  )

  useEffect(() => {
    const b = brandingData?.data
    if (!b || b.default) return
    const root = document.documentElement
    if (b.primaryColor) root.style.setProperty('--color-primary', b.primaryColor)
    if (b.secondaryColor) root.style.setProperty('--color-secondary', b.secondaryColor)
    if (b.faviconUrl) {
      const favicon = document.querySelector('link[rel="icon"]')
      if (favicon) favicon.href = b.faviconUrl
    }
    return () => {
      root.style.removeProperty('--color-primary')
      root.style.removeProperty('--color-secondary')
    }
  }, [brandingData])

  const controlsByDomain = React.useMemo(() => {
    const grouped = {}
    controls.forEach(control => {
      const domainKey = str(control.domain)
      if (!grouped[domainKey]) grouped[domainKey] = []
      grouped[domainKey].push(control)
    })
    return grouped
  }, [controls])

  const progress = React.useMemo(() => {
    if (controls.length === 0) return 0
    const answered = controls.filter(c => c.response?.maturityLevel !== undefined).length
    return Math.round((answered / controls.length) * 100)
  }, [controls])

  useEffect(() => {
    if (controls.length > 0) {
      setLocalNotes(prev => {
        const next = { ...prev }
        controls.forEach(control => {
          if (control.response?._id && !(control.response._id in next)) {
            next[control.response._id] = control.response?.notes || ''
          }
        })
        return next
      })
    }
  }, [controls])

  useEffect(() => {
    const timers = debounceTimers.current
    return () => { Object.values(timers).forEach(clearTimeout) }
  }, [])

  const updateResponseMutation = useMutation(
    ({ controlResponseId, maturityLevel, notes }) =>
      updateVendorResponse(token, controlResponseId, { maturityLevel, notes }),
    {
      onSuccess: () => { queryClient.invalidateQueries(['vendor-assessment', token]) },
      onError: () => {
        Dialog.alert({ icon: 'error', title: t('gap-analysis:assessment.vendorForm.swal.errorTitle'), message: t('gap-analysis:assessment.vendorForm.swal.errorSaveResponse') })
      }
    }
  )

  const updateNotesMutation = useMutation(
    ({ controlResponseId, notes }) => updateVendorResponse(token, controlResponseId, { notes }),
    {
      onError: () => {
        Dialog.alert({ icon: 'error', title: t('gap-analysis:assessment.vendorForm.swal.errorTitle'), message: t('gap-analysis:assessment.vendorForm.swal.errorSaveNotes') })
      }
    }
  )

  const uploadMutation = useMutation(
    ({ controlResponseId, formData }) => uploadEvidence(token, controlResponseId, formData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['vendor-assessment', token])
        Dialog.fire({ icon: 'info', title: t('gap-analysis:assessment.vendorForm.swal.uploadedTitle'), text: t('gap-analysis:assessment.vendorForm.swal.uploadedText'), timer: 2000, showConfirmButton: false })
      },
      onError: (error) => {
        Dialog.alert({ icon: 'error', title: t('gap-analysis:assessment.vendorForm.swal.uploadFailedTitle'), message: error.response?.data?.message || t('gap-analysis:assessment.vendorForm.swal.uploadFailedTitle') })
      }
    }
  )

  const submitMutation = useMutation(
    () => submitVendorAssessment(token),
    {
      onSuccess: () => {
        Dialog.alert({ icon: 'success', title: t('gap-analysis:assessment.vendorForm.swal.submittedTitle'), message: t('gap-analysis:assessment.vendorForm.swal.submittedHtml'), confirmLabel: 'Close' })
      },
      onError: (error) => {
        Dialog.alert({ icon: 'error', title: t('gap-analysis:assessment.vendorForm.swal.submitFailedTitle'), message: error.response?.data?.message || t('gap-analysis:assessment.vendorForm.swal.submitFailedText') })
      }
    }
  )

  const handleMaturityChange = (controlResponseId, maturityLevel) => {
    updateResponseMutation.mutate({ controlResponseId, maturityLevel })
  }

  const handleNotesChange = useCallback((controlResponseId, notes) => {
    setLocalNotes(prev => ({ ...prev, [controlResponseId]: notes }))
    if (debounceTimers.current[controlResponseId]) clearTimeout(debounceTimers.current[controlResponseId])
    debounceTimers.current[controlResponseId] = setTimeout(() => {
      updateNotesMutation.mutate({ controlResponseId, notes })
    }, 800)
  }, [updateNotesMutation])

  const handleFileUpload = (controlResponseId, event) => {
    const file = event.target.files[0]
    if (!file) return
    const formData = new FormData()
    formData.append('file', file)
    uploadMutation.mutate({ controlResponseId, formData })
  }

  const handleSubmit = async () => {
    const confirmed = await Dialog.confirm({
      title: t('gap-analysis:assessment.vendorForm.swal.submitTitle'),
      message: t('gap-analysis:assessment.vendorForm.swal.submitHtml', { progress }),
      confirmLabel: t('gap-analysis:assessment.vendorForm.swal.submitButton'),
    })
    if (confirmed) submitMutation.mutate()
  }

  if (assessmentLoading) {
    return (
      <PageWrapper>
        <LoadingWrapper>
          <Spinner />
        </LoadingWrapper>
      </PageWrapper>
    )
  }

  if (!assessment) {
    return (
      <PageWrapper>
        <ErrorContainer>
          <ErrorTitle>{t('gap-analysis:assessment.vendorForm.invalidLink')}</ErrorTitle>
          <ErrorText>{t('gap-analysis:assessment.vendorForm.invalidLinkText')}</ErrorText>
        </ErrorContainer>
      </PageWrapper>
    )
  }

  const domainEntries = Object.entries(controlsByDomain)

  return (
    <PageWrapper>
      <ExternalHeader>
        <HeaderLeft>
          <HeaderLogo src="/img/logo-horizontal.png" alt="Virtual Officer" />
          <HeaderDivider />
          <HeaderCompanyInfo>
            <HeaderRequestedLabel>{t('gap-analysis:assessment.vendorForm.header.requestedByLabel')}</HeaderRequestedLabel>
            <HeaderCompanyName>{str(assessment.companyName)}</HeaderCompanyName>
          </HeaderCompanyInfo>
        </HeaderLeft>
        <HeaderRight>
          <HeaderDot />
          <HeaderLinkStatus>{t('gap-analysis:assessment.vendorForm.header.linkActive')}</HeaderLinkStatus>
        </HeaderRight>
      </ExternalHeader>
    <PageContainer>
      <Section>
        <InfoCard>
          <HeaderTitle>{t('gap-analysis:assessment.vendorForm.title')}</HeaderTitle>
          <HeaderMeta>
            {t('gap-analysis:assessment.vendorForm.requestedBy')} <strong>{str(assessment.companyName)}</strong>
            {' · '}
            {t('gap-analysis:assessment.vendorForm.framework')} <strong>{str(assessment.frameworkName)}</strong>
          </HeaderMeta>
          <ProgressSection>
            <ProgressLabelRow>
              <ProgressLabel>{t('gap-analysis:assessment.vendorForm.progress')}</ProgressLabel>
              <ProgressValue>{progress}%</ProgressValue>
            </ProgressLabelRow>
            <ProgressTrack>
              <ProgressFill $progress={progress} />
            </ProgressTrack>
          </ProgressSection>
        </InfoCard>

        <InstructionAlert>
          <InfoCircleSVG />
          <InstructionText>{t('gap-analysis:assessment.vendorForm.instructions')}</InstructionText>
        </InstructionAlert>

        {domainEntries.map(([domain, domainControls], domainIdx) => {
          const isOpen = expandedDomainIndex === domainIdx
          const domainAnswered = domainControls.filter(c => c.response?.maturityLevel !== undefined).length

          return (
            <AccordionCard key={domain} ref={el => { accordionRefs.current[domainIdx] = el }}>
              <AccordionHeader
                onClick={() => toggleDomain(domainIdx)}
              >
                <AccordionHeaderLeft>
                  <DomainName>{translateDomain(t, domain)}</DomainName>
                  <DomainProgressBadge>
                    {domainAnswered}/{domainControls.length} {t('gap-analysis:assessment.vendorForm.answered')}
                  </DomainProgressBadge>
                </AccordionHeaderLeft>
                <ChevronWrapper $open={isOpen}>
                  <ChevronSVG />
                </ChevronWrapper>
              </AccordionHeader>

              {isOpen && (
                <AccordionBody>
                  {domainControls.map((control, idx) => {
                    const isLast = idx === domainControls.length - 1
                    const isDisabled = !!control.response?.locked

                    return (
                      <ControlWrapper key={control._id} $last={isLast}>
                        <ControlHeader>
                          <ControlTitle>
                            {idx + 1}. {str(control.description)}
                          </ControlTitle>
                          {isDisabled && (
                            <LockedBadge>
                              <LockIcon width={12} height={12} />
                              {t('gap-analysis:assessment.vendorForm.locked')}
                            </LockedBadge>
                          )}
                        </ControlHeader>

                        <MaturityRadioGroup>
                          {maturityLevels.map(level => {
                            const isSelected = control.response?.maturityLevel === level.value
                            return (
                              <RadioLabel
                                key={level.value}
                                $selected={isSelected}
                                $disabled={isDisabled}
                                htmlFor={`${control._id}-${level.value}`}
                              >
                                <RadioInput
                                  type="radio"
                                  id={`${control._id}-${level.value}`}
                                  name={`maturity-${control._id}`}
                                  value={level.value}
                                  checked={isSelected}
                                  disabled={isDisabled}
                                  onChange={() => handleMaturityChange(control.response._id, level.value)}
                                />
                                <div>
                                  <LevelLabel>{level.label}</LevelLabel>
                                  <LevelDesc>{level.description}</LevelDesc>
                                </div>
                              </RadioLabel>
                            )
                          })}
                        </MaturityRadioGroup>

                        <TextField
                          multiline
                          fullWidth
                          rows={2}
                          placeholder={t('gap-analysis:assessment.vendorForm.additionalNotes')}
                          value={localNotes[control.response?._id] ?? control.response?.notes ?? ''}
                          onChange={(e) => handleNotesChange(control.response._id, e.target.value)}
                          disabled={isDisabled}
                        />

                        {!!control.evidenceRequired && (
                          <EvidenceSection>
                            <EvidenceLabel>
                              {t('gap-analysis:assessment.vendorForm.evidenceRequired')}
                            </EvidenceLabel>
                            <StyledUploadButton
                              type="button"
                              disabled={uploadMutation.isLoading || isDisabled}
                            >
                              <CloudUploadIcon width={16} height={16} />
                              {t('gap-analysis:assessment.vendorForm.uploadEvidence')}
                              <input
                                type="file"
                                hidden
                                accept=".pdf,.png,.jpg,.jpeg"
                                onChange={(e) => handleFileUpload(control.response._id, e)}
                              />
                            </StyledUploadButton>

                            {control.response?.evidenceFiles?.some(f => f.scanStatus === 'pending') && (
                              <RefreshButton
                                type="button"
                                onClick={() => queryClient.invalidateQueries(['vendor-assessment', token])}
                              >
                                <RefreshIcon width={14} height={14} />
                                {t('gap-analysis:assessment.vendorForm.checkScanStatus')}
                              </RefreshButton>
                            )}

                            {control.response?.evidenceFiles?.length > 0 && (
                              <FileList>
                                {control.response.evidenceFiles.map((file) => {
                                  const scanDisplay = getScanStatusDisplay(file.scanStatus, t)
                                  return (
                                    <FileItem key={file._id}>
                                      <FileIconWrapper $color={scanDisplay.color} title={scanDisplay.label}>
                                        {scanDisplay.icon}
                                      </FileIconWrapper>
                                      <FileDetails>
                                        <FileName>{file.fileName}</FileName>
                                        <FileMeta $malicious={file.scanStatus === 'malicious'}>
                                          {file.scanStatus === 'malicious'
                                            ? `${t('gap-analysis:assessment.vendorForm.scanStatus.threatDetected')}${file.scanResultDetail ? ': ' + file.scanResultDetail : ''}`
                                            : file.scanStatus === 'pending'
                                              ? t('gap-analysis:assessment.vendorForm.scanStatus.scanning')
                                              : `${(file.fileSize / 1024).toFixed(1)} KB`
                                          }
                                        </FileMeta>
                                      </FileDetails>
                                      <ScanChip $variant={scanDisplay.chipColor}>{scanDisplay.label}</ScanChip>
                                    </FileItem>
                                  )
                                })}
                              </FileList>
                            )}
                          </EvidenceSection>
                        )}
                      </ControlWrapper>
                    )
                  })}
                </AccordionBody>
              )}
            </AccordionCard>
          )
        })}

        <ActionRow>
          {(updateResponseMutation.isLoading || updateNotesMutation.isLoading) && (
            <SavingIndicator>{t('gap-analysis:assessment.vendorForm.saving')}</SavingIndicator>
          )}
          <SubmitButton
            type="button"
            onClick={handleSubmit}
            disabled={submitMutation.isLoading || progress < 100}
          >
            {submitMutation.isLoading ? (
              <Spinner $size={20} $borderColor="color-mix(in srgb, var(--vo-white) 60%, transparent)" />
            ) : (
              <><SendIcon width={18} height={18} /> {t('gap-analysis:assessment.vendorForm.submitButton')}</>
            )}
          </SubmitButton>
        </ActionRow>
      </Section>
    </PageContainer>
    </PageWrapper>
  )
}
